import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { EmployeeV2Dto, EmployeeRestService, EmployerDto, UserDto } from '../api/exa';
import { AuthService } from './auth.service';
import { EmployerService } from './employer.service';
import { ErrorHandlerService } from './error-handler.service';
import { NGXLogger } from 'ngx-logger';
import { ExaGrant } from '../model/exa-grant';
import { EmployeeStateEnum } from '../enums/employee-state-enum';
import { AlertService } from './alert.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class EmployeeService {
  private employerDto: EmployerDto = null;

  private employees_active: EmployeeV2Dto[] = [];
  private employees_active$ = new BehaviorSubject<EmployeeV2Dto[]>([]);
  private employees_activeWD: EmployeeV2Dto[] = [];
  private employees_activeWD$ = new BehaviorSubject<EmployeeV2Dto[]>([]);
  private employees_inactive: EmployeeV2Dto[] = [];
  private employees_inactive$ = new BehaviorSubject<EmployeeV2Dto[]>([]);
  private employees_inactiveWD: EmployeeV2Dto[] = [];
  private employees_inactiveWD$ = new BehaviorSubject<EmployeeV2Dto[]>([]);

  readonly NO_EMPLOYEE: EmployeeV2Dto = { id: -1, employeeId: -1 };

  constructor(
    private auth: AuthService,
    private employerService: EmployerService,
    private employeeRest: EmployeeRestService,
    private errorHandler: ErrorHandlerService,
    private alertService: AlertService,
    private translate: TranslateService,
    private log: NGXLogger
  ) {
    this.employerService.onSelectEmployer().subscribe((employer) => {
      this.employerDto = employer;
      if (this.employerDto) {
        this.reloadEmployeesActive();
        // this.reloadEmployeesInactive();
      } else {
        this.employees_active = [];
        this.employees_activeWD = [];
        this.employees_inactive = [];
        this.employees_inactiveWD = [];
        this.employees_active$.next(this.employees_active);
        this.employees_activeWD$.next(this.employees_activeWD);
        this.employees_inactive$.next(this.employees_inactive);
        this.employees_inactiveWD$.next(this.employees_inactiveWD);
      }
    });
    this.log.debug('Construct');
  }

  reloadEmployeesActive() {
    this.employees_active = [];
    this.employees_activeWD = [];
    if (this.employerDto && this.auth.hasGrantOwner(ExaGrant.READ_EMPLOYEE, this.employerDto.id)) {
      this.employeeRest.getEmployeesUsingPOST(this.employerDto.username, { size: 200 }).subscribe(
        (employees_active) => {
          this.employees_activeWD = employees_active.content;
          this.employees_active = this.filterDeletedElements(this.employees_activeWD);
          this.employees_active$.next(this.employees_active);
          this.employees_activeWD$.next(this.employees_activeWD);
        },
        (err) => {
          this.employees_active = [];
          this.employees_activeWD = [];
          this.employees_active$.next(this.employees_active);
          this.employees_activeWD$.next(this.employees_activeWD);
          this.errorHandler.handleResponseError(err);
        }
      );
    } else {
      this.employees_active$.next(this.employees_active);
      this.employees_activeWD$.next(this.employees_activeWD);
    }
  }

  // reloadEmployeesInactive() {
  //   this.employees_inactive = [];
  //   this.employees_inactiveWD = [];
  //   if (this.employerDto && this.auth.hasGrantOwner(ExaGrant.READ_EMPLOYEE, this.employerDto.id)) {
  //     this.employeeRest.getEmployeesUsingPOST(this.employerDto.username).subscribe(
  //       (employees_inactive) => {
  //         this.employees_inactiveWD = employees_inactive.content;
  //         this.employees_inactive = this.filterDeletedElements(this.employees_inactiveWD);
  //         this.employees_inactive$.next(this.employees_inactive);
  //         this.employees_inactiveWD$.next(this.employees_inactiveWD);
  //       },
  //       (err) => {
  //         this.employees_inactive = [];
  //         this.employees_inactiveWD = [];
  //         this.employees_inactive$.next(this.employees_inactive);
  //         this.employees_inactiveWD$.next(this.employees_inactiveWD);
  //         this.errorHandler.handleResponseError(err);
  //       }
  //     );
  //   } else {
  //     this.employees_inactive$.next(this.employees_inactive);
  //     this.employees_inactiveWD$.next(this.employees_inactiveWD);
  //   }
  // }

  private filterDeletedElements(elementsWithDeleted: EmployeeV2Dto[]): EmployeeV2Dto[] {
    return elementsWithDeleted.filter((elem) => !elem.deleted);
  }

  onGetEmployeesActive(withDeleted?: boolean): Observable<EmployeeV2Dto[]> {
    if (withDeleted) {
      return this.employees_activeWD$;
    }
    return this.employees_active$;
  }

  sortEmployees(empls: EmployeeV2Dto[]): EmployeeV2Dto[] {
    const ret = empls.sort(function (a, b) {
      return a.username.toLowerCase().localeCompare(b.username.toLowerCase());
    });
    return ret;
  }

  addEmptyEmployeeFirst(empls: EmployeeV2Dto[]): EmployeeV2Dto[] {
    const ret = [...empls];
    ret.unshift(this.NO_EMPLOYEE);
    return ret;
  }

  onGetEmployeesInactive(withDeleted?: boolean): Observable<EmployeeV2Dto[]> {
    if (withDeleted) {
      return this.employees_inactiveWD$;
    }
    return this.employees_inactive$;
  }

  getEmployeeByUsername(employeeName: string) {
    if (employeeName) {
      for (let employee of this.employees_activeWD) {
        if (employee.username == employeeName) {
          return employee;
        }
      }
      for (let employee of this.employees_inactiveWD) {
        if (employee.username == employeeName) {
          return employee;
        }
      }
    }
    return null;
  }

  getEmployeeById(employeeId: number) {
    if (employeeId) {
      for (let employee of this.employees_activeWD) {
        if (employee.employeeId == employeeId) {
          return employee;
        }
      }
      for (let employee of this.employees_inactiveWD) {
        if (employee.employeeId == employeeId) {
          return employee;
        }
      }
    }
    return null;
  }

  getFormattedNameById(employeeId: number) {
    if (employeeId) {
      const empl: EmployeeV2Dto = this.getEmployeeById(employeeId);
      return this.getFormattedName(empl);
    }
    return '';
  }

  getFormattedNameByUsername(employeeUsername: string) {
    if (employeeUsername && employeeUsername.trim()) {
      const empl: EmployeeV2Dto = this.getEmployeeByUsername(employeeUsername);
      return this.getFormattedName(empl);
    }
    return '';
  }

  getFormattedName(employeeDto: UserDto) {
    if (employeeDto) {
      if (employeeDto.firstName && employeeDto.lastName) {
        return employeeDto.firstName + ' ' + employeeDto.lastName + ' (' + employeeDto.username + ')';
      } else if (employeeDto.firstName) {
        return employeeDto.firstName + ' (' + employeeDto.username + ')';
      } else if (employeeDto.lastName) {
        return employeeDto.lastName + ' (' + employeeDto.username + ')';
      } else {
        return employeeDto.username;
      }
    }
    return '';
  }

  onGetEmployee(employeeId: number): Promise<EmployeeV2Dto> {
    return new Promise<EmployeeV2Dto>((resolve, reject) => {
      this.employeeRest.getEmployeeByIdUsingGET(employeeId).subscribe(
        (result) => {
          // Success
          // this.alertService.openMessageToast(this.translate.instant('EMPLOYEE.ADD.SUCCESS'));
          console.log(' Employee erfolgreich abgefragt.');

          resolve(result);
        },
        (err) => {
          this.errorHandler.handleResponseError(err);
          reject(err);
        }
      );
    });
  }

  addEmployee(enterpriseUsername: string, employeeInfo: EmployeeV2Dto): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.employeeRest.addEmployeeUsingPOST(enterpriseUsername, employeeInfo).subscribe(
        (result) => {
          // Success
          this.alertService.openMessageToast(this.translate.instant('EMPLOYEE.ADD.SUCCESS'));
          console.log(' Employee hinzugefügt.');
          this.reloadEmployeesActive();
          // this.reloadEmployeesInactive();

          resolve();
        },
        (err) => {
          this.errorHandler.handleResponseError(err);
          reject(err);
        }
      );
    });
  }

  createEmployee(enterpriseUsername: string, employeeInfo: EmployeeV2Dto): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.employeeRest.createEmployeeUsingPOST(enterpriseUsername, employeeInfo).subscribe(
        (result) => {
          // Success
          this.alertService.openMessageToast(this.translate.instant('EMPLOYEE.CREATE.SUCCESS'));
          console.log(' Employee erstellt.');
          this.reloadEmployeesActive();
          // this.reloadEmployeesInactive();

          resolve();
        },
        (err) => {
          this.errorHandler.handleResponseError(err);
          reject(err);
        }
      );
    });
  }

  deleteEmployee(employeeId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.employeeRest.deleteEmployeeByIdUsingDELETE(employeeId).subscribe(
        (result) => {
          // Success
          this.alertService.openMessageToast(this.translate.instant('EMPLOYEES.DELETE.SUCCESS'));
          console.log(' Employee delete -> SUCCESS');
          // this.reloadEmployeesActive();
          // this.reloadEmployeesInactive();
          resolve();
        },
        (err) => {
          this.errorHandler.handleResponseError(err);
          console.log(' employee delete -> ERROR');
          reject(err);
        }
      );
    });
  }

  editEmployee(employeeUsername: string, enterpriseUsername: string, updatedEmployeeDto: EmployeeV2Dto): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.employeeRest.updateEmployeeUsingPUT(employeeUsername, enterpriseUsername, updatedEmployeeDto).subscribe(
        (result) => {
          // Success
          this.alertService.openMessageToast(this.translate.instant('EMPLOYEE.EDIT.SUCCESS'));
          console.log(' Employee edit  -> SUCCESS');
          this.reloadEmployeesActive();
          // this.reloadEmployeesInactive();
          resolve();
        },
        (err) => {
          this.errorHandler.handleResponseError(err);
          console.log(' employee edit ERROR: ', err);
          reject(err);
        }
      );
    });
  }
}
